import {
  forwardRef,
  cloneElement,
  isValidElement,
  type AriaAttributes,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { Spinner } from "./Spinner";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "accent"
  | "success"
  | "warning";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  asChild?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

const baseClasses =
  "relative inline-flex items-center justify-center gap-2 font-medium select-none transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 disabled:opacity-60 disabled:cursor-not-allowed";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 shadow-sm shadow-indigo-500/20 hover:shadow-md hover:shadow-indigo-500/40",
  secondary:
    "text-text-primary bg-surface-raised border border-border-default hover:bg-gray-600",
  ghost:
    "text-text-primary bg-transparent border border-border-subtle hover:bg-white/5",
  danger:
    "text-white bg-danger hover:bg-red-700 shadow-sm shadow-red-500/20 hover:shadow-red-500/40",
  accent:
    "text-white bg-indigo-600 hover:bg-indigo-500 shadow-sm shadow-indigo-500/20 hover:shadow-md hover:shadow-indigo-500/30",
  success:
    "text-white bg-green-600 hover:bg-green-500 shadow-sm shadow-green-500/20",
  warning:
    "text-yellow-100 bg-yellow-500/15 border border-yellow-500/30 hover:bg-yellow-500/25",
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: "rounded-md px-2.5 py-1.5 text-xs",
  sm: "rounded-md px-3 py-2 text-sm gap-1.5",
  md: "rounded-xl px-4 py-2 text-sm",
  lg: "rounded-xl px-8 py-4 text-base font-semibold",
};

const spinnerSize: Record<ButtonSize, number> = {
  xs: 12,
  sm: 14,
  md: 18,
  lg: 20,
};

type CloneableProps = AriaAttributes & {
  className?: string;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    iconStart,
    iconEnd,
    asChild = false,
    fullWidth = false,
    disabled,
    className,
    children,
    type,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;
  const classes = clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    fullWidth && "w-full",
    !isDisabled && "cursor-pointer",
    className
  );

  if (asChild && isValidElement(children)) {
    const child = children as ReactElement<CloneableProps>;
    return cloneElement(child, {
      className: clsx(classes, child.props.className),
      "aria-busy": loading || undefined,
      "aria-disabled": isDisabled || undefined,
      ...rest,
    });
  }

  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      {...rest}
    >
      <span
        className={clsx(
          "inline-flex items-center",
          size === "xs" ? "gap-1.5" : "gap-2",
          loading && "invisible"
        )}
      >
        {iconStart}
        {children}
        {iconEnd}
      </span>
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size={spinnerSize[size]} />
        </span>
      )}
    </button>
  );
});

export default Button;
